// Canvas constants
export const CANVAS_WIDTH = 1600;   // Fixed canvas width
export const CANVAS_HEIGHT = 1000;  // Fixed canvas height
export const CANVAS_BACKGROUND = '#ffffff';  // White background

// Drawing element types
export type SchemaElementType = 'rect' | 'line' | 'polygon' | 'circle' | 'text' | 'freehand' | 'icon' | 'road' | 'trail';

// Icon types for template elements
export type IconType =
  | 'building' | 'building-large' | 'room' | 'wall' | 'door' | 'castle' | 'tower'
  | 'fence' | 'barricade' | 'sandbags' | 'wire'
  | 'tree' | 'bush' | 'crate' | 'vehicle' | 'rock'
  | 'spawn' | 'objective' | 'safe' | 'danger'
  | 'waypoint' | 'target' | 'flag' | 'bomb'
  | 'tires' | 'barrel' | 'ship' | 'airplane' | 'helicopter' | 'trailer'
  | 'road' | 'trail' | 'river' | 'lake' | 'bridge' | 'trench' | 'exit';

export interface SchemaElement {
  id: string;
  type: SchemaElementType;
  x: number;           // 0-1600 range
  y: number;           // 0-1000 range
  width?: number;
  height?: number;
  radius?: number;
  points?: number[];   // For lines, polygons, freehand
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  text?: string;
  fontSize?: number;   // For text elements (default 16)
  label?: string;
  rotation?: number;
  locked?: boolean;    // Prevent accidental movement
  iconType?: IconType; // For icon elements - determines the visual representation
}

// Node positioning
export interface NodePosition {
  nodeId: string;
  nodeCode: string;
  nodeType: string;
  friendlyName: string;
  positionX: number | null;  // 0-1600 range
  positionY: number | null;  // 0-1000 range
  scale: number;             // 0.5 - 2.0 range
  locked: boolean;           // Prevent accidental movement
  isMainNode: boolean;
}

// Full node with position data from API
export interface NodeWithPosition {
  id: string;
  saleNumber: string;
  nodeCode: string;
  nodeType: string;
  fieldId: string | null;
  mapId: string | null;
  position: string | null;
  friendlyName: string;
  soldAt: string | null;
  isActive: boolean;
  positionX: number | null;
  positionY: number | null;
  scale: number;
  locked: boolean;
  createdAt: string;
  updatedAt: string;
}

// Operation templates
export interface OperationTemplate {
  id: string;
  fieldId: string;
  mapId: string;
  operationFieldConfigsId: string;
  name: string;
  description: string;
  devices: TemplateDevicePosition[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateDevicePosition {
  fmdId: string;           // field_mission_devices ID
  ofcId: string;           // Operation field config ID (which purchase)
  deviceCode: string;      // Device code for marking
  deviceName: string;
  deviceType: string;
  model: string;
  iconUrl?: string;        // Device icon URL (from device catalog)
  positionX: number;       // 0-1600 range
  positionY: number;       // 0-1000 range
  scale: number;           // 0.5 - 2.0 range
  locked: boolean;         // Prevent accidental movement
  displayNumber: number;
}

// Editor state
export type EditorTool = 'select' | 'rect' | 'line' | 'polygon' | 'circle' | 'text' | 'freehand' | 'eraser' | 'pan' | 'border' | 'road' | 'trail';

export interface EditorState {
  tool: EditorTool;
  fillColor: string;
  strokeColor: string;
  strokeWidth: number;
  showGrid: boolean;
  snapToGrid: boolean;
  zoom: number;
  isFullscreen: boolean;
  selectedElementIds: string[];  // Multi-select support
  clipboard: SchemaElement[];    // Copy/paste support
}

// Editor mode
export type EditorMode = 'schema' | 'nodes' | 'devices';

// Template library categories
export type TemplateCategory = 'structures' | 'barriers' | 'cover' | 'zones' | 'markers';

export interface TemplateItem {
  id: string;
  name: string;
  category: TemplateCategory;
  icon: string;  // SVG path or component name
  element: Partial<SchemaElement>;  // Pre-configured element
}

// Map data from API
export interface MapData {
  id: string;
  fieldId: string;
  name: string;
  description: string;
  schemaImageUrl: string;
  schemaVideoUrl: string;
  images: string[];
  youtubeVideos: string[];
  fieldNodesIds: string[];
  schemaElements: SchemaElement[];
  canvasBgColor: 'dark' | 'white';
  exportedImageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

// Undo/Redo history
export interface HistoryState {
  past: SchemaElement[][];
  present: SchemaElement[];
  future: SchemaElement[][];
}

// Position update request
export interface NodePositionUpdate {
  nodeId: string;
  positionX: number;
  positionY: number;
  scale: number;
  locked: boolean;
}

// Schema elements update request
export interface SchemaElementsUpdate {
  elements: SchemaElement[];
}

// Operation template request
export interface OperationTemplateRequest {
  id?: string;  // If provided, update existing; otherwise create new
  fieldId: string;
  mapId: string;
  operationFieldConfigsId: string;
  name: string;
  description: string;
  devices: TemplateDevicePosition[];
}

// Device catalog item (for placing on map)
export interface DeviceCatalogItem {
  id: string;
  name: string;
  type: string;
  description: string;
  iconUrl?: string;
}

// Keyboard shortcuts
export const KEYBOARD_SHORTCUTS = {
  SAVE: 'ctrl+s',
  UNDO: 'ctrl+z',
  REDO: 'ctrl+shift+z',
  COPY: 'ctrl+c',
  PASTE: 'ctrl+v',
  DELETE: 'delete',
  ESCAPE: 'escape',
  LOCK: 'l',
  FULLSCREEN: 'f11',
} as const;

// Default editor state
export const DEFAULT_EDITOR_STATE: EditorState = {
  tool: 'select',
  fillColor: 'rgba(100, 100, 100, 0.5)',
  strokeColor: '#000000',
  strokeWidth: 2,
  showGrid: false,
  snapToGrid: false,
  zoom: 1,
  isFullscreen: false,
  selectedElementIds: [],
  clipboard: [],
};

// Grid settings
export const GRID_SIZE = 20;
export const GRID_COLOR = 'rgba(255, 255, 255, 0.1)';

// Scale limits
export const MIN_SCALE = 0.5;
export const MAX_SCALE = 2.0;
export const SCALE_STEP = 0.1;

// Zoom limits
export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 3;
export const ZOOM_STEP = 0.1;

// Auto-save interval (in milliseconds)
export const AUTO_SAVE_INTERVAL = 3 * 60 * 1000; // 3 minutes
